import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Avatar,
  DocumentAttachment,
  ImageGallery,
  ListItemImageAttachment,
  VideoAttachment,
  VideoGallery,
} from 'components';
import { DEFAULT_DATE_FORMAT, DETAILS_FILE_NAME_LENGTH_LIMIT } from 'constants/index';
import { useModal } from 'hooks';
import { theme } from 'styles';
import { EquipmentPayload, EquipmentStatus } from 'types';
import { UserExtendedDetails } from 'types/employee';
import { formattedDate, getFullName, hideDocNameMidPart } from 'utils';

import { categoryOptions } from '../constants';
import {
  ActionButton,
  ActionsW,
  AttachmentsW,
  Chip,
  ChipsRow,
  CommentSection,
  CommentText,
  ContainerW,
  CustomFieldRow,
  DetailsCard,
  DocsGridW,
  EquipmentName,
  FieldLabel,
  FieldSection,
  FieldValue,
  HeaderSection,
  MediaColumnW,
  MediaSectionW,
  SectionLabel,
  SectionW,
  UserName,
  UserSection,
} from './styled';

interface EquipmentDetailsProps {
  equipment: EquipmentPayload;
  assignedUser: UserExtendedDetails | null;
  canEdit: boolean;
  canDelete: boolean;
  canConfirm?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onConfirm?: () => void;
}

export function EquipmentDetails({
  equipment,
  assignedUser,
  canEdit,
  canDelete,
  canConfirm,
  onEdit,
  onDelete,
  onConfirm,
}: EquipmentDetailsProps): ReactElement {
  const { t } = useTranslation();

  const {
    isOpen: isImageGalleryOpen,
    handleOpenModal: openImageGallery,
    handleCloseModal: closeImageGallery,
  } = useModal();

  const {
    isOpen: isVideoGalleryOpen,
    handleOpenModal: openVideoGallery,
    handleCloseModal: closeVideoGallery,
  } = useModal();

  const categoryLabel =
    categoryOptions.find((opt) => opt.value === equipment.category)?.label ?? equipment.category;
  const isActive = equipment.status === EquipmentStatus.ACTIVE;

  const imagesList = equipment.attachments?.filter((a) => a.type === 'image') ?? [];
  const videosList = equipment.attachments?.filter((a) => a.type === 'video') ?? [];
  const docsList = equipment.attachments?.filter((a) => a.type === 'document') ?? [];

  const assignedUserName = assignedUser
    ? getFullName({ firstName: assignedUser.firstName, lastName: assignedUser.lastName })
    : '';

  const customFields = (equipment.custom_fields ?? []).filter((f) => f.key || f.value);

  const attachmentWrapperSx = { width: '74px', height: '74px', marginRight: '0px' };

  return (
    <ContainerW>
      <DetailsCard>
        <HeaderSection>
          <EquipmentName>{equipment.name}</EquipmentName>
          {(canConfirm || canEdit || canDelete) && (
            <ActionsW>
              {canConfirm && (
                <ActionButton
                  startIcon={
                    <CheckIcon
                      style={{
                        fill: theme.palette.common.white,
                      }}
                    />
                  }
                  onClick={onConfirm}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.common.white,
                    '&:hover': { backgroundColor: theme.palette.success.dark },
                  }}
                >
                  <span className="btn-label">{t('equipments.actions.confirm')}</span>
                </ActionButton>
              )}
              {canEdit && !canConfirm && (
                <ActionButton
                  startIcon={
                    <CreateIcon
                      style={{
                        fill: theme.palette.common.white,
                      }}
                    />
                  }
                  onClick={onEdit}
                  sx={{
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.common.white,
                    '&:hover': { backgroundColor: theme.palette.info.dark },
                  }}
                >
                  <span className="btn-label">{t('equipments.actions.editDetails')}</span>
                </ActionButton>
              )}
              {canDelete && (
                <ActionButton
                  startIcon={<DeleteIcon />}
                  onClick={onDelete}
                  sx={{
                    backgroundColor: theme.palette.common.white,
                    color: theme.palette.primary.main,
                    border: '1px solid #ddd',
                  }}
                >
                  <span className="btn-label">{t('equipments.actions.delete')}</span>
                </ActionButton>
              )}
            </ActionsW>
          )}
        </HeaderSection>

        <ChipsRow>
          <Chip chipvariant="category">{categoryLabel}</Chip>
          <Chip chipvariant={isActive ? 'active' : 'requested'}>
            {t(isActive ? 'equipments.register.title' : 'equipments.request.title')}
          </Chip>
        </ChipsRow>

        {assignedUser && (
          <UserSection>
            <Avatar size="s" userId={assignedUser.id} filename={assignedUser.avatar?.small ?? ''} />
            <UserName>{assignedUserName}</UserName>
          </UserSection>
        )}

        {!!equipment.serial && (
          <FieldSection>
            <FieldLabel>{t('equipments.create.form.placeholders.serialNumber')}</FieldLabel>
            <FieldValue>{equipment.serial}</FieldValue>
          </FieldSection>
        )}

        {!!equipment.ordered_at && (
          <FieldSection>
            <FieldLabel>{t('equipments.create.form.placeholders.orderedOn')}</FieldLabel>
            <FieldValue>
              {formattedDate(new Date(equipment.ordered_at), DEFAULT_DATE_FORMAT)}
            </FieldValue>
          </FieldSection>
        )}

        {!!equipment.expire_at && (
          <FieldSection>
            <FieldLabel>{t('equipments.create.form.placeholders.expirationDate')}</FieldLabel>
            <FieldValue>
              {formattedDate(new Date(equipment.expire_at), DEFAULT_DATE_FORMAT)}
            </FieldValue>
          </FieldSection>
        )}

        {!!equipment.comment && (
          <CommentSection>
            <FieldLabel>{t('equipments.create.form.placeholders.comment')}</FieldLabel>
            <CommentText dangerouslySetInnerHTML={{ __html: equipment.comment }} />
          </CommentSection>
        )}

        {customFields.length > 0 && (
          <SectionW>
            <SectionLabel>{t('equipments.create.form.placeholders.customFields')}</SectionLabel>
            {customFields.map((field, i) => (
              <CustomFieldRow key={i}>
                <FieldLabel>{field.key}</FieldLabel>
                <FieldValue>{field.value}</FieldValue>
              </CustomFieldRow>
            ))}
          </SectionW>
        )}

        {docsList.length > 0 && (
          <SectionW>
            <SectionLabel>{t('equipments.attachments.filter.documents')}</SectionLabel>
            <DocsGridW>
              {docsList.map((doc) => {
                const { id: docId, file, file_url, file_extension } = doc;
                const fileName =
                  file.original.length > DETAILS_FILE_NAME_LENGTH_LIMIT
                    ? hideDocNameMidPart({
                        docName: file.original,
                        limit: DETAILS_FILE_NAME_LENGTH_LIMIT,
                      })
                    : file.original;
                return (
                  <DocumentAttachment
                    key={docId}
                    attachmentId={docId}
                    fileName={fileName}
                    file_extension={file_extension}
                    handleDelete={() => null}
                    hasDownload
                    originalFileName={file.original}
                    originalFileUrl={file_url?.original}
                    blockDelete
                  />
                );
              })}
            </DocsGridW>
          </SectionW>
        )}

        {(imagesList.length > 0 || videosList.length > 0) && (
          <MediaSectionW>
            {imagesList.length > 0 && (
              <MediaColumnW>
                <SectionLabel>{t('equipments.attachments.filter.images')}</SectionLabel>
                <AttachmentsW>
                  {imagesList.map((img) => (
                    <ListItemImageAttachment
                      key={img.id}
                      attachmentId={img.id}
                      fileName={img.file.small}
                      fileUrl={img.file_url?.small}
                      wrapperSx={attachmentWrapperSx}
                      onOpenGallery={openImageGallery}
                    />
                  ))}
                </AttachmentsW>
              </MediaColumnW>
            )}
            {videosList.length > 0 && (
              <MediaColumnW>
                <SectionLabel>{t('equipments.attachments.filter.videos')}</SectionLabel>
                <AttachmentsW>
                  {videosList.map((vid) => (
                    <VideoAttachment
                      key={vid.id}
                      attachmentId={vid.file_url.video_uid}
                      onOpenGallery={openVideoGallery}
                      wrapperSx={attachmentWrapperSx}
                      blockDelete
                    />
                  ))}
                </AttachmentsW>
              </MediaColumnW>
            )}
          </MediaSectionW>
        )}
      </DetailsCard>

      {isImageGalleryOpen && (
        <ImageGallery
          open={isImageGalleryOpen}
          onClose={closeImageGallery}
          imageList={imagesList}
        />
      )}

      {isVideoGalleryOpen && (
        <VideoGallery
          open={isVideoGalleryOpen}
          onClose={closeVideoGallery}
          videoList={videosList}
        />
      )}
    </ContainerW>
  );
}
